import * as turf from '@turf/turf';
import { GeoJSONSource, Map as MaplibreMap, MapMouseEvent } from 'maplibre-gl';
import { v4 } from 'uuid';
import { EventBus } from '../../../app/contexts/event-bus';
import assert from '../../../utils/assert';
import { DrawingObjectType } from '../types';
import { FeDrawMode } from './draw-mode';

export class FeRectangleMode extends FeDrawMode {
    constructor(
        private readonly map: MaplibreMap,
        private readonly source: GeoJSONSource
    ) {
        super();
    }

    private clickCount = 0;
    private feature: GeoJSON.Feature<GeoJSON.Polygon> | null = null;
    private vertexA: [number, number] | null = null;
    private vertexB: [number, number] | null = null;

    private createNew(a: [number, number]) {
        this.feature = {
            id: v4(),
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                coordinates: [[a, a]]
            },
            properties: {}
        };

        this.source.updateData({
            add: [this.feature]
        });
    }

    override onClick(e: MapMouseEvent): void {
        if (this.clickCount === 0) {
            this.vertexA = e.lngLat.toArray();
            this.createNew(this.vertexA);
            this.clickCount = 1;
        } else {
            this.finishRectangle();
        }
    }

    override onKeyDown(e: KeyboardEvent): void {
        if (e.key !== ' ' && e.key !== 'Enter') {
            return;
        }

        if (this.clickCount > 0) {
            e.preventDefault();
            this.finishRectangle();
        }
    }

    override onMouseMove(e: MapMouseEvent): void {
        if (this.clickCount === 0) {
            return;
        }

        this.vertexB = e.lngLat.toArray();
        this.updateGeometry();
    }

    override cancel(): void {
        this.clickCount = 0;
        if (this.feature) {
            this.source.setData(turf.featureCollection([]));
        }
    }

    private finishRectangle() {
        this.clickCount = 0;
        assert(this.vertexA);
        assert(this.feature);
        const center = turf.center(this.feature).geometry.coordinates;
        const bbox = turf.bbox(this.feature);
        const width = Math.abs(bbox[2] - bbox[0]);
        const height = Math.abs(bbox[3] - bbox[1]);
        EventBus.fire('draw.create', {
            drawObject: {
                type: DrawingObjectType.rectangle,
                center: center,
                width: width,
                height: height
            }
        });
    }

    private updateGeometry() {
        assert(this.vertexA);
        assert(this.vertexB);
        assert(this.feature);

        this.feature.geometry = turf.bboxPolygon([...this.vertexA, ...this.vertexB]).geometry;
        this.source.updateData({
            update: [
                {
                    id: this.feature.id as string,
                    newGeometry: this.feature.geometry
                }
            ]
        });
    }
}
